
import React from 'react';
import CalendarEvent from '@/components/CalendarEvent';
import { ProcessedEvent } from '@/utils/calendarEventLayout';
import { format } from 'date-fns';

interface MultiDayEventsLayerProps {
  processedEvents: ProcessedEvent[];
  onEventClick: (event: any) => void;
  cellWidth: number;
}

const MultiDayEventsLayer: React.FC<MultiDayEventsLayerProps> = ({
  processedEvents,
  onEventClick,
  cellWidth
}) => {
  // Define constants for calculations to avoid magic numbers
  const eventHorizontalPadding = 4; // 2px padding on each side
  const rowHeight = 32; // Height per row
  const verticalOffset = 4; // Small top offset
  const rowGap = 4; // Gap between rows
  const borderWidth = 1; // Border width between cells
  
  return (
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
      {processedEvents.map((processedEvent, index) => {
        // Calculate event span in days
        const span = processedEvent.endDayIndex - processedEvent.startDayIndex + 1;
        
        // Calculate width in pixels based on days spanned and current cell width
        // For multiple days, we need to account for the borders between days
        const eventWidth = span * cellWidth - (2 * eventHorizontalPadding);
        
        // Calculate left position based on starting day index
        // No need to add border width for each day as the cells already account for borders
        const leftPosition = processedEvent.startDayIndex * cellWidth + eventHorizontalPadding;
        
        // Calculate top position based on row height and vertical position
        const topPosition = (processedEvent.verticalPosition * rowHeight) + verticalOffset;
        
        return (
          <CalendarEvent
            key={`multi-${processedEvent.event.id}-${index}`}
            event={processedEvent.event}
            onClick={onEventClick}
            cellWidth={cellWidth}
            isMultiDay={true}
            startDayIndex={processedEvent.startDayIndex}
            endDayIndex={processedEvent.endDayIndex}
            verticalPosition={processedEvent.verticalPosition}
            style={{
              width: `${eventWidth}px`,
              height: `${rowHeight - rowGap}px`, // Fixed height with small gap
              left: `${leftPosition}px`,
              top: `${topPosition}px`,
              position: 'absolute',
              pointerEvents: 'auto', // Enable pointer events on the event
              zIndex: 10 + processedEvent.verticalPosition // Events higher in position stack on top
            }}
          />
        );
      })}
    </div>
  );
};

export default MultiDayEventsLayer;
